import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCheck,
  CircleMinus,
  CircleX,
  EllipsisVertical,
  OctagonAlert,
  Settings,
} from "lucide-react";
import { useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { TooltipWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { NameEmailAvatarRound } from "components/shared/shared";
import CustomTable from "components/shared/table";
import { GetUserIfLoggedIn, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Role, UserMembership } from "data/models";
import { deleteOrganisationMember } from "data/mutations-common";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";

type Props = {
  memberships: UserMembership[];
  isLoading: boolean;
};

interface InvitationRow {
  id: string;
  orgId: string;
  orgName: string;
  orgEmail: string;
  email: string;
  name: string;
  role?: Role;
  status: string;
}

const emptyInvitation: UserMembership = {
  organisationId: "",
  userMemberId: "",
  status: "",
  organisationName: "",
  userFullName: "",
  userEmail: "",
  organisationEmail: "",
  userFirstName: "",
  userLastName: "",
};

export const InvitationList = ({ memberships, isLoading }: Props) => {
  const [modal, setModal] = useState<{
    isOpen: boolean;
    invitation: UserMembership;
  }>({
    isOpen: false,
    invitation: emptyInvitation,
  });
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const queryClient = useQueryClient();
  const user = GetUserIfLoggedIn(identity) || undefined;
  const history = useHistory();

  const handleClose = () => {
    setModal((prevState) => ({ ...prevState, isOpen: false }));
  };

  const handleManageMember = (row: InvitationRow) => {
    if (row.id && row.orgId) {
      history.push(
        generatePath(Routes.orgMemberDetails, {
          accountId: row.id,
          orgId: row.orgId,
        }),
      );
    }
  };

  const handleRemoveClick = (row: InvitationRow) => {
    setModal({
      isOpen: true,
      invitation: {
        organisationId: row.orgId,
        userMemberId: row.id,
        status: row.status,
        organisationName: row.orgName,
        userFullName: row.name,
        userEmail: row.email,
        organisationEmail: row.orgEmail,
        userFirstName: "",
        userLastName: "",
      },
    });
  };

  const deleteMemberMutation = useMutation({
    mutationFn: async (invitation: UserMembership) => {
      return deleteOrganisationMember(
        invitation.organisationId,
        invitation.userMemberId,
      );
    },
    onSuccess: (_, invitation) => {
      queryClient.setQueryData<UserMembership[]>(
        [QueryKeys.organisationInvitations, identityId],
        (oldData) => {
          if (!oldData) return [];
          return oldData.filter(
            (member) => member.userEmail !== invitation.userEmail,
          );
        },
      );
      handleClose();
    },
  });

  const handleDelete = () => {
    deleteMemberMutation.mutate(modal.invitation);
  };

  const columns: ColumnDef<InvitationRow>[] = [
    {
      accessorKey: "name",
      header: "Members",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex h-full w-full items-center">
          <NameEmailAvatarRound
            name={row.original.name}
            variant="circular"
            isLoading={false}
            email={row.original.email}
            isAdmin={false}
          />
        </div>
      ),
    },
    {
      accessorKey: "role",
      header: () => <div className="w-full text-right">Role</div>,
      size: 70,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="text-right">
          {row.original.role
            ? row.original.role.charAt(0).toUpperCase() +
              row.original.role.slice(1)
            : "Admin"}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: () => <div className="w-full text-right">Status</div>,
      size: 90,
      enableSorting: false,
      cell: ({ row }) => <div className="text-right">{row.original.status}</div>,
    },
    {
      id: "actions",
      header: "",
      size: 50,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex h-full w-full items-center justify-end">
          <RowActionsMenu
            row={row.original}
            currentUserEmail={user?.email}
            onManage={handleManageMember}
            onRemove={handleRemoveClick}
          />
        </div>
      ),
    },
  ];

  const developerNameOrEmail: string = modal.invitation.userFullName
    ? modal.invitation.userFullName
    : modal.invitation.userEmail;

  return (
    <div className="my-4">
      {deleteMemberMutation.isSuccess && (
        <div className="mb-1 pt-2">
          <Alert variant="success">
            <CheckCheck className="size-4" />
            <AlertTitle>User has been deleted from the organisation</AlertTitle>
          </Alert>
        </div>
      )}

      {deleteMemberMutation.isError && (
        <div className="mb-1 pt-2">
          <Alert variant="destructive">
            <OctagonAlert className="size-4" />
            <AlertTitle>
              {(deleteMemberMutation.error as Error)?.message ||
                "An error occurred while deleting the member"}
            </AlertTitle>
          </Alert>
        </div>
      )}

      <ConfirmationModal
        isLoading={deleteMemberMutation.isPending}
        isError={deleteMemberMutation.isError}
        open={modal.isOpen}
        header={"Remove this member?"}
        body={`Are you sure you want to remove "${developerNameOrEmail}" from "${modal.invitation.organisationName}"? They will no longer be able to access this org unless invited again.`}
        leftButtonText={"CANCEL"}
        leftButtonColor="primary"
        rightButtonText={"REMOVE MEMBER"}
        rightButtonColor="error"
        handleClose={handleClose}
        handleLeftButton={handleClose}
        handleRightButton={handleDelete}
      />

      <CustomTable
        rows={invitationsToRows(memberships)}
        columns={columns}
        isLoading={isLoading}
        overlayText="No members found"
      />
    </div>
  );
};

interface RowActionsMenuProps {
  row: InvitationRow;
  currentUserEmail: string | undefined;
  onManage: (row: InvitationRow) => void;
  onRemove: (row: InvitationRow) => void;
}

const RowActionsMenu = ({
  row,
  currentUserEmail,
  onManage,
  onRemove,
}: RowActionsMenuProps) => {
  const deletePermissionKeys = [PermissionsKeys.membersDelete];
  const { isPermitted, permissionDeniedMessageString } = usePermissions();

  const canManage = !!row.id && !!row.orgId && !!row.name;
  const isLeave = !!currentUserEmail && currentUserEmail === row.email;
  const isRemoveDisabled =
    !currentUserEmail ||
    !isPermitted(deletePermissionKeys) ||
    row.role === Role.OWNER;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <EllipsisVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => onManage(row)}
          disabled={!canManage}
        >
          <Settings />
          Manage Member
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <TooltipWrapper
          title={permissionDeniedMessageString(deletePermissionKeys)}
        >
          <DropdownMenuItem
            onClick={() => onRemove(row)}
            disabled={isRemoveDisabled}
            variant={isRemoveDisabled ? undefined : "destructive"}
          >
            {isLeave ? (
              <>
                <CircleX />
                Leave
              </>
            ) : (
              <>
                <CircleMinus />
                Remove
              </>
            )}
          </DropdownMenuItem>
        </TooltipWrapper>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const invitationsToRows = (
  invitationList: UserMembership[],
): InvitationRow[] => {
  return invitationList
    .filter((i) => i.status === "Accepted" || i.status === "Pending")
    .sort((a, b) => a.userFullName.localeCompare(b.userFullName))
    .map((key) => ({
      id: key.userMemberId,
      orgId: key.organisationId,
      orgName: key.organisationName,
      orgEmail: key.organisationEmail,
      email: key.userEmail,
      name: key.userFullName,
      role: key.role,
      status: key.status == "Pending" ? "Invited" : key.status,
    }));
};
